import { TREE_ATLAS } from './treeAtlas'
import type { CardTreeOptions } from './treeGeometry'

/**
 * The three trees on this mountain, now cast from photographs.
 *
 * Same three things happening to conifers at altitude as before — a sheltered
 * fir caked into a snow ghost, a spruce holding its shape, an exposed snag
 * stripped to a spar — but each is now anchored to an actual photo in the
 * atlas rather than to a palette of needle colours. The shape parameters
 * (whorls, spread, droop, gaps) still drive where branch cards hang on the
 * near builds; the photographs decide what a branch looks like.
 *
 * In its own module because three things must agree on it: the scene that
 * instances the trees, the budget report, and anyone tuning the look.
 */

export type ArchetypeKind = 'ghost' | 'spruce' | 'spar'
export const ARCHETYPE_ORDER: readonly ArchetypeKind[] = ['ghost', 'spruce', 'spar']

/** Unit tree height. Instances scale from this, so it is a shared convention. */
export const UNIT_HEIGHT = 9

/** Forest share per archetype, for the budget report's weighted average. */
export const ARCHETYPE_SHARE: Record<ArchetypeKind, number> = {
  ghost: 0.44,
  spruce: 0.36,
  spar: 0.20,
}

type Build = Record<'near' | 'far', CardTreeOptions>

export const TREE_ARCHETYPES: Record<ArchetypeKind, Build> = {
  ghost: {
    // Heavily rimed: the branch cards carry the pillowed snow loaves, packed
    // dense and narrow the way rime builds where branches already crowd.
    near: {
      height: UNIT_HEIGHT, style: 'cards',
      treeCell: TREE_ATLAS.ghost, branchCell: TREE_ATLAS.branchHeavy,
      whorls: 16, branchesPerWhorl: 8,
      spread: 0.185, droop: 0.42, gaps: 0.06, windFlag: 0.0, seed: 0x5eed01,
    },
    far: {
      height: UNIT_HEIGHT, style: 'star',
      treeCell: TREE_ATLAS.ghost, branchCell: TREE_ATLAS.branchHeavy,
      whorls: 0, branchesPerWhorl: 0,
      spread: 0, droop: 0, gaps: 0, windFlag: 0, seed: 0x5eed01,
    },
  },
  spruce: {
    // Green showing between snow-laden whorls.
    near: {
      height: UNIT_HEIGHT, style: 'cards',
      treeCell: TREE_ATLAS.spruce, branchCell: TREE_ATLAS.branchMedium,
      whorls: 17, branchesPerWhorl: 8,
      spread: 0.205, droop: 0.36, gaps: 0.08, windFlag: 0.08, seed: 0x5eed02,
    },
    far: {
      height: UNIT_HEIGHT, style: 'star',
      treeCell: TREE_ATLAS.spruce, branchCell: TREE_ATLAS.branchMedium,
      whorls: 0, branchesPerWhorl: 0,
      spread: 0, droop: 0, gaps: 0, windFlag: 0.08, seed: 0x5eed02,
    },
  },
  spar: {
    // The snag is its photograph at every distance. Its entire character is
    // the silhouette of one particular dead tree — leaning bole, flagged crown
    // streaming to one side — and assembling that from branch sprays would
    // throw away exactly what the photo knows. The wind-bearing yaw in
    // obstacleMesh points every spar's lean the same way.
    near: {
      height: UNIT_HEIGHT, style: 'star',
      treeCell: TREE_ATLAS.spar, branchCell: TREE_ATLAS.branchSparse,
      whorls: 0, branchesPerWhorl: 0,
      spread: 0, droop: 0, gaps: 0, windFlag: 0.8, seed: 0x5eed03,
    },
    far: {
      height: UNIT_HEIGHT, style: 'star',
      treeCell: TREE_ATLAS.spar, branchCell: TREE_ATLAS.branchSparse,
      whorls: 0, branchesPerWhorl: 0,
      spread: 0, droop: 0, gaps: 0, windFlag: 0.8, seed: 0x5eed03,
    },
  },
}
